import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toastrError, toastrSuccess } from "lib/toastr";
import { FiInfo, FiMail } from "react-icons/fi";
import { SkeletonRows } from "components/ui";
import {
  PreferenceView,
  getEmailPreferences,
  updateEmailPreferences,
} from "./EmailPreferences.api";

/**
 * Account-level email preference centre.
 *
 * Follows the existing Account/MyProfile pattern — react-query for load and
 * mutate, toastr for feedback — rather than introducing a parallel one.
 *
 * The three marketing purposes are independently controllable, which is the whole
 * point: someone who does not want newsletters may still want to be told about
 * surveys they can take. Opting out of one leaves the others alone.
 *
 * Transactional email is deliberately absent from this screen. Password resets,
 * verification codes, security alerts and payout confirmations are a function of
 * having an account, not a preference, and showing them here would imply they
 * can be switched off.
 */
export const EmailPreferences = () => {
  const [draft, setDraft] = useState<Record<string, boolean>>({});

  const { data, isLoading, refetch } = useQuery(["emailPreferences"], getEmailPreferences, {
    refetchOnWindowFocus: false,
  });

  const preferences: PreferenceView[] = useMemo(() => data?.result ?? [], [data]);

  // Reset the draft whenever the server state changes, so the toggles always
  // reflect what is actually stored rather than a stale local edit.
  useEffect(() => {
    setDraft(Object.fromEntries(preferences.map((p) => [p.purpose, p.granted])));
  }, [preferences]);

  const mutation = useMutation(
    () => {
      // Send only what actually changed. A no-op save writes no audit row
      // server-side, but there is no reason to ask it to consider unchanged
      // values at all.
      const changed = Object.fromEntries(
        preferences
          .filter((p) => draft[p.purpose] !== p.granted)
          .map((p) => [p.purpose, draft[p.purpose]])
      );
      return updateEmailPreferences(changed);
    },
    {
      onSuccess: (response: any) => {
        if (response?.success) {
          toastrSuccess(response.message ?? "Your email preferences have been updated.");
          refetch();
        } else {
          toastrError(response?.message ?? "We could not save your preferences.");
        }
      },
      onError: () => toastrError("We could not save your preferences. Please try again."),
    }
  );

  const dirty = preferences.some((p) => draft[p.purpose] !== p.granted);

  if (isLoading) {
    return (
      <div className="zs-card">
        <p className="px-5 pt-5 text-sm text-ink-3">Loading your email preferences…</p>
        <SkeletonRows rows={3} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <section className="zs-card">
        <div className="zs-card-header">
          <div className="flex items-start min-w-0 gap-3">
            <span className="inline-flex items-center justify-center rounded w-9 h-9 shrink-0 bg-primary-soft text-primary-text">
              <FiMail />
            </span>
            <div className="min-w-0">
              <h2 className="zs-card-title">Marketing emails</h2>
              <p className="mt-0.5 zs-card-subtitle">
                Choose which marketing emails you would like to receive. Each type is separate, so turning
                one off does not affect the others.
              </p>
            </div>
          </div>
        </div>

        <div className="divide-y divide-line">
          {preferences.map((preference) => (
            <div
              key={preference.purpose}
              className="flex items-start gap-3 px-5 py-4 transition-colors hover:bg-surface-hover/60"
            >
              <input
                id={`pref-${preference.purpose}`}
                type="checkbox"
                checked={draft[preference.purpose] ?? false}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, [preference.purpose]: e.target.checked }))
                }
                className="mt-0.5 h-4 w-4 shrink-0 accent-primary cursor-pointer"
              />
              <label htmlFor={`pref-${preference.purpose}`} className="flex-1 mb-0 cursor-pointer">
                <span className="block text-sm font-medium text-ink">{preference.label}</span>
                <span className="block mt-0.5 text-[13px] leading-snug text-ink-3">
                  {preference.description}
                </span>
                {preference.status === "NONE" ? (
                  <span className="block mt-1.5 text-xs text-ink-4">
                    You have not chosen this yet, so these emails are not being sent.
                  </span>
                ) : null}
              </label>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-3 px-5 py-4 border-t sm:flex-row sm:items-center sm:justify-between border-line bg-surface-2 rounded-b-lg">
          <p className="text-xs text-ink-3">{dirty ? "You have unsaved changes." : "All changes saved."}</p>
          <button
            type="button"
            disabled={!dirty || mutation.isLoading}
            onClick={() => mutation.mutate()}
            className="zs-btn zs-btn-primary"
          >
            {mutation.isLoading ? "Saving…" : "Save preferences"}
          </button>
        </div>
      </section>

      {/*
        Stated plainly so nobody believes turning everything off stops account
        email. Transactional mail is not gated and never will be.
      */}
      <div className="flex items-start gap-3 p-4 text-xs leading-relaxed border rounded-lg border-line bg-surface-2 text-ink-3">
        <FiInfo className="w-4 h-4 mt-px shrink-0 text-info" />
        <p>
          You will still receive essential emails about your account — password resets, verification
          codes, security alerts and payout confirmations. These are not marketing and cannot be turned
          off while your account is open.
        </p>
      </div>
    </div>
  );
};

export default EmailPreferences;
